import { useCallback, useEffect, useRef, useState } from "react";

import { DeploymentOverlay } from "../components/DeploymentOverlay.js";
import { BlockPinInput } from "../components/landing/BlockPinInput.js";
import { OptionChips } from "../components/landing/OptionChips.js";
import { VaultScene } from "../components/VaultScene.js";
import { deployWebenv, deployWebenvFork } from "../deploy/index.js";
import { errorProgress, initialProgress } from "../deploy/progress.js";
import {
  DEFAULT_CUSTOM_OPTIONS,
  DEFAULT_FORK_OPTIONS,
  DEFAULT_FRESH_OPTIONS,
  estimateBootTime,
  SEED_PRESET_STEPS,
} from "../runtime/environment-options.js";
import {
  forkConfigFromPreset,
  forkConfigFromUrl,
  humanizeRpcError,
  validateRpcUrl,
} from "../runtime/fork-config.js";
import { createTevmClients } from "../runtime/tevm-client.js";

import type { DeploymentProgressState } from "../hooks/useDeploymentProgress.js";
import type {
  BlockTimeMode,
  CustomRpcOptions,
  ForkModeOptions,
  FreshModeOptions,
  FundAmount,
} from "../runtime/environment-options.js";
import type { TevmConfig } from "../runtime/types.js";
import type { UniswapDeployment } from "@gotts.ai/devenv";
import type { Chain, PublicClient, TestClient, WalletClient } from "viem";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LandingProps {
  progress: DeploymentProgressState;
  onDeployComplete: (
    clients: {
      publicClient: PublicClient;
      walletClient: WalletClient;
      testClient: TestClient;
      worker: Worker;
      chain: Chain;
    },
    deployment: UniswapDeployment,
    blockTime: BlockTimeMode,
  ) => void;
}

// ---------------------------------------------------------------------------
// Mode definitions
// ---------------------------------------------------------------------------

const MODES = [
  {
    id: "from-scratch",
    title: "Start Fresh",
    subtitle: "Local deterministic stack",
    description:
      "Clean chain for full-cycle testing. Deploy all Uniswap contracts from scratch on a local in-browser EVM.",
    chainId: 31337,
    estimate: "~15s",
    signal: "Pipeline",
    context: "Deterministic",
  },
  {
    id: "fork-ethereum",
    title: "Fork Ethereum",
    subtitle: "Mainnet state mirror",
    description:
      "Real pools, realistic routing behavior. Fork mainnet state and overlay Uniswap V4 contracts.",
    chainId: 1,
    estimate: "~2s",
    signal: "Liquidity",
    context: "Mainnet",
  },
  {
    id: "fork-base",
    title: "Fork Base",
    subtitle: "L2 execution mirror",
    description:
      "Low-fee, fast-block environment. Fork Base L2 state for optimistic rollup testing.",
    chainId: 8453,
    estimate: "~2s",
    signal: "Throughput",
    context: "L2",
  },
] as const;

/** Total selectable items: 3 preset modes + custom RPC. */
const ITEM_COUNT = MODES.length + 1;

const FUND_AMOUNT_OPTIONS: { value: string; label: string }[] = [
  { value: "1", label: "1 ETH" },
  { value: "10", label: "10 ETH" },
  { value: "100", label: "100 ETH" },
];

// ---------------------------------------------------------------------------
// Landing
// ---------------------------------------------------------------------------

export function Landing({ progress, onDeployComplete }: LandingProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [rpcUrl, setRpcUrl] = useState("");
  const [rpcError, setRpcError] = useState<string | null>(null);
  const [rpcValidating, setRpcValidating] = useState(false);
  const [rpcDetected, setRpcDetected] = useState<{
    chainId: number;
    blockNumber: bigint;
  } | null>(null);

  // Per-mode options
  const [freshOptions, _setFreshOptions] = useState<FreshModeOptions>(
    DEFAULT_FRESH_OPTIONS,
  );
  const [forkOptions, setForkOptions] =
    useState<ForkModeOptions>(DEFAULT_FORK_OPTIONS);
  const [customOptions, setCustomOptions] = useState<CustomRpcOptions>(
    DEFAULT_CUSTOM_OPTIONS,
  );

  const rpcInputRef = useRef<HTMLInputElement>(null);
  const deployGenRef = useRef(0);

  // --- Deploy engine ---

  const handleCancel = useCallback(() => {
    deployGenRef.current++;
    progress.reset();
    setLaunching(false);
  }, [progress]);

  /** Get the active block time for the currently selected mode. */
  const getActiveBlockTime = useCallback((): BlockTimeMode => {
    const selectedMode =
      selectedIndex < MODES.length ? MODES[selectedIndex] : null;
    if (!selectedMode) return customOptions.blockTime;
    if (selectedMode.id === "from-scratch") return freshOptions.blockTime;
    return forkOptions.blockTime;
  }, [selectedIndex, freshOptions, forkOptions, customOptions]);

  const startDeploy = useCallback(
    async (config: TevmConfig, blockTime: BlockTimeMode) => {
      deployGenRef.current++;
      const gen = deployGenRef.current;
      setLaunching(true);
      progress.startDeployment(config);

      try {
        const clients = await createTevmClients(config);
        let deployment: UniswapDeployment;

        if (config.mode === "from-scratch") {
          const seedSteps = SEED_PRESET_STEPS[freshOptions.seedPreset];
          deployment = await deployWebenv({
            clients,
            onProgress: progress.updateProgress,
            seedSteps,
          });
        } else {
          deployment = await deployWebenvFork({
            clients,
            config,
            onProgress: progress.updateProgress,
          });
        }

        if (gen !== deployGenRef.current) return;
        onDeployComplete(clients, deployment, blockTime);
      } catch (err) {
        if (gen !== deployGenRef.current) return;
        const rawMsg = err instanceof Error ? err.message : String(err);
        const friendlyMsg = humanizeRpcError(rawMsg);
        // eslint-disable-next-line no-console
        console.error("Deployment failed:", err);
        const isFork = config.mode !== "from-scratch";
        progress.updateProgress(
          errorProgress(initialProgress(isFork), friendlyMsg, 0),
        );
        setLaunching(false);
      }
    },
    [progress, onDeployComplete, freshOptions.seedPreset],
  );

  const startMode = useCallback(
    (index: number) => {
      const mode = MODES[index];
      if (!mode) return;
      const blockTime = getActiveBlockTime();
      if (mode.id === "from-scratch") {
        void startDeploy({ mode: "from-scratch", chainId: 31337 }, blockTime);
      } else if (mode.id === "fork-ethereum") {
        const forkBlock =
          forkOptions.forkBlock === "latest"
            ? undefined
            : forkOptions.forkBlock;
        void startDeploy(
          forkConfigFromPreset("ethereum", {
            blockNumber: forkBlock,
            fundAmountEth: forkOptions.fundAmount,
          }),
          blockTime,
        );
      } else {
        const forkBlock =
          forkOptions.forkBlock === "latest"
            ? undefined
            : forkOptions.forkBlock;
        void startDeploy(
          forkConfigFromPreset("base", {
            blockNumber: forkBlock,
            fundAmountEth: forkOptions.fundAmount,
          }),
          blockTime,
        );
      }
    },
    [startDeploy, forkOptions, getActiveBlockTime],
  );

  const submitCustomRpc = useCallback(async () => {
    if (!rpcUrl || rpcValidating) return;
    setRpcValidating(true);
    setRpcError(null);
    const result = await validateRpcUrl(rpcUrl);
    setRpcValidating(false);

    if (!result.valid) {
      setRpcError(result.error ?? "Invalid RPC URL");
      setRpcDetected(null);
      return;
    }

    setRpcDetected({
      chainId: result.chainId ?? 1,
      blockNumber: result.blockNumber ?? 0n,
    });

    const forkBlock =
      customOptions.forkBlock === "latest"
        ? result.blockNumber
        : customOptions.forkBlock;

    void startDeploy(
      forkConfigFromUrl(rpcUrl, result.chainId ?? 1, forkBlock),
      customOptions.blockTime,
    );
  }, [rpcUrl, rpcValidating, startDeploy, customOptions]);

  const handleItemClick = useCallback(
    (index: number) => {
      if (launching) return;
      setSelectedIndex(index);
    },
    [launching],
  );

  const handleConfirm = useCallback(() => {
    if (launching) return;
    if (selectedIndex < MODES.length) {
      startMode(selectedIndex);
    } else if (rpcUrl) {
      void submitCustomRpc();
    }
  }, [launching, selectedIndex, startMode, rpcUrl, submitCustomRpc]);

  // --- Keyboard navigation ---

  useEffect(() => {
    if (launching) return;

    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      switch (e.key) {
        case "ArrowUp":
        case "k":
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + ITEM_COUNT) % ITEM_COUNT);
          break;
        case "ArrowDown":
        case "j":
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % ITEM_COUNT);
          break;
        case "Enter":
          e.preventDefault();
          if (selectedIndex < MODES.length) startMode(selectedIndex);
          else if (rpcUrl) void submitCustomRpc();
          break;
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [launching, selectedIndex, startMode, rpcUrl, submitCustomRpc]);

  // Auto-focus RPC input when custom is selected
  useEffect(() => {
    if (selectedIndex === MODES.length) {
      setTimeout(() => {
        rpcInputRef.current?.focus();
      }, 50);
    }
  }, [selectedIndex]);

  // --- Derived ---

  const selectedMode =
    selectedIndex < MODES.length ? MODES[selectedIndex] : null;
  const isCustom = selectedIndex >= MODES.length;
  const isFreshMode = selectedMode?.id === "from-scratch";
  const isForkMode =
    selectedMode?.id === "fork-ethereum" || selectedMode?.id === "fork-base";

  const bootEstimate = isFreshMode
    ? estimateBootTime(freshOptions.seedPreset)
    : (selectedMode?.estimate ?? "~2s");

  const canConfirm = selectedMode ?? (isCustom && rpcUrl && !rpcValidating);
  const confirmLabel = isCustom ? "Connect" : "Deploy";

  // --- Render ---

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col"
      style={{ background: "var(--color-black)" }}
    >
      {/* Three.js vault corridor background */}
      <VaultScene
        className="landing-bg"
        active={!launching}
        progress={progress.smoothPercent / 100}
      />

      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-8 sm:px-6 sm:py-10">
        <div className="w-full max-w-3xl">
          {/* Panels */}
          <div
            className="grid gap-4 md:grid-cols-[1fr_1fr]"
            style={{
              opacity: 0,
              animation: "landing-enter 500ms var(--ease-luxury) 250ms both",
            }}
          >
            {/* Left — environment list */}
            <div className="landing-panel">
              <div className="landing-panel-header">
                <span className="flex-1">Environments</span>
                <span
                  className="font-mono text-[9px]"
                  style={{
                    color: "var(--color-text-muted)",
                    opacity: 0.6,
                    letterSpacing: "0.06em",
                  }}
                >
                  Select one
                </span>
              </div>
              <div className="py-1">
                {MODES.map((mode, i) => (
                  <button
                    key={mode.id}
                    onClick={() => {
                      handleItemClick(i);
                    }}
                    disabled={launching}
                    className="landing-list-item w-full"
                    data-selected={selectedIndex === i || undefined}
                  >
                    <span className="landing-list-accent" />
                    <span className="landing-list-dot" />
                    <span className="landing-list-label">{mode.title}</span>
                    <span className="landing-list-meta">
                      {String(mode.chainId)}
                    </span>
                  </button>
                ))}

                <div className="gold-divider mx-4 my-1.5" />

                <button
                  onClick={() => {
                    handleItemClick(MODES.length);
                  }}
                  disabled={launching}
                  className="landing-list-item w-full"
                  data-selected={isCustom || undefined}
                >
                  <span className="landing-list-accent" />
                  <span className="landing-list-dot" />
                  <span className="landing-list-label">Custom RPC</span>
                </button>
              </div>
            </div>

            {/* Right — details + deploy button */}
            <div className="landing-panel flex flex-col">
              <div className="landing-panel-header">Details</div>
              <div
                className="flex-1 p-4"
                key={selectedIndex}
                style={{ animation: "landing-detail-in 200ms ease" }}
              >
                {selectedMode ? (
                  <div className="space-y-4">
                    <div>
                      <h2
                        className="font-mono text-sm"
                        style={{ color: "var(--color-text)" }}
                      >
                        {selectedMode.title}
                      </h2>
                      <p
                        className="mt-0.5 font-mono text-[11px]"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        {selectedMode.subtitle}
                      </p>
                    </div>

                    <div className="gold-divider" />

                    <div className="space-y-2">
                      {(
                        [
                          {
                            key: "Chain",
                            value: String(selectedMode.chainId),
                          },
                          { key: "Boot", value: bootEstimate },
                          { key: "Signal", value: selectedMode.signal },
                          { key: "Context", value: selectedMode.context },
                        ] as const
                      ).map((kv) => (
                        <div key={kv.key} className="landing-kv">
                          <span className="landing-kv-key">{kv.key}</span>
                          <span className="landing-kv-dots" />
                          <span className="landing-kv-value">{kv.value}</span>
                        </div>
                      ))}
                    </div>

                    <p
                      className="font-mono text-[11px] leading-relaxed"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      {selectedMode.description}
                    </p>

                    {isForkMode && (
                      <>
                        <div className="gold-divider" />
                        <div className="space-y-3">
                          <BlockPinInput
                            value={forkOptions.forkBlock}
                            onChange={(v) => {
                              setForkOptions((prev) => ({
                                ...prev,
                                forkBlock: v,
                              }));
                            }}
                            disabled={launching}
                          />
                          <OptionChips
                            label="Fund Amount"
                            options={FUND_AMOUNT_OPTIONS}
                            selected={String(forkOptions.fundAmount)}
                            onChange={(v) => {
                              setForkOptions((prev) => ({
                                ...prev,
                                fundAmount: Number(v) as FundAmount,
                              }));
                            }}
                            disabled={launching}
                            suffix="per account"
                          />
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h2
                        className="font-mono text-sm"
                        style={{ color: "var(--color-text)" }}
                      >
                        Custom RPC
                      </h2>
                      <p
                        className="mt-0.5 font-mono text-[11px]"
                        style={{ color: "var(--color-text-muted)" }}
                      >
                        Connect any EVM endpoint
                      </p>
                    </div>

                    <div className="gold-divider" />

                    <input
                      ref={rpcInputRef}
                      type="url"
                      value={rpcUrl}
                      onChange={(e) => {
                        setRpcUrl(e.target.value);
                        setRpcDetected(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && rpcUrl) {
                          void submitCustomRpc();
                        }
                      }}
                      placeholder="https://rpc.example.com"
                      className="landing-input w-full"
                      disabled={launching || rpcValidating}
                    />

                    <BlockPinInput
                      value={customOptions.forkBlock}
                      onChange={(v) => {
                        setCustomOptions((prev) => ({
                          ...prev,
                          forkBlock: v,
                        }));
                      }}
                      disabled={launching}
                    />

                    {rpcDetected && (
                      <p
                        className="font-mono text-[10px]"
                        style={{ color: "var(--color-gold-dim)" }}
                      >
                        Detected: Chain {rpcDetected.chainId} at block{" "}
                        {rpcDetected.blockNumber.toLocaleString()}
                      </p>
                    )}

                    {rpcError && (
                      <p
                        className="font-mono text-[10px]"
                        style={{ color: "var(--color-error)" }}
                      >
                        {rpcError}
                      </p>
                    )}

                    {rpcValidating && (
                      <p
                        className="font-mono text-[10px]"
                        style={{ color: "var(--color-gold-dim)" }}
                      >
                        Validating endpoint...
                      </p>
                    )}

                    <p
                      className="font-mono text-[11px] leading-relaxed"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      Fork any EVM-compatible chain. Webenv will detect the
                      network and overlay Uniswap contracts.
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm button — always visible at bottom of details panel */}
              <div
                className="border-t px-4 py-3"
                style={{ borderColor: "rgba(201, 168, 76, 0.12)" }}
              >
                <button
                  onClick={handleConfirm}
                  disabled={launching || !canConfirm}
                  className="landing-confirm-btn w-full"
                >
                  {launching ? (
                    <span className="landing-confirm-label flex items-center justify-center gap-2">
                      <span className="landing-spinner" />
                      Initializing...
                    </span>
                  ) : (
                    <>
                      <span className="landing-confirm-label">
                        {confirmLabel}
                      </span>
                      <span className="landing-confirm-hint">
                        {selectedMode
                          ? selectedMode.title
                          : rpcUrl
                            ? rpcUrl.replace(/^https?:\/\//, "").slice(0, 30)
                            : "enter URL above"}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Action bar — keyboard hints */}
          <div
            className="landing-action-bar mt-4 hidden sm:flex"
            style={{
              opacity: 0,
              animation: "landing-enter 500ms var(--ease-luxury) 400ms both",
            }}
          >
            <span
              className="font-mono text-[10px]"
              style={{ letterSpacing: "0.08em" }}
            >
              Select environment, then deploy.
            </span>
            <div className="flex items-center gap-4">
              <span className="action-bar-item">
                <span className="action-bar-key">{"\u2191\u2193"}</span>
                Navigate
              </span>
              <span className="action-bar-item">
                <span className="action-bar-key">{"\u23CE"}</span>
                Deploy
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Deployment overlay */}
      {(progress.isDeploying || progress.isComplete) && (
        <DeploymentOverlay
          progress={progress.progress}
          smoothPercent={progress.smoothPercent}
          liveElapsedMs={progress.liveElapsedMs}
          onRetry={handleCancel}
          onCancel={handleCancel}
        />
      )}

      {/* Persistent error banner */}
      {progress.error && !progress.isDeploying && (
        <div
          className="octa-sm fixed bottom-8 left-1/2 z-40 -translate-x-1/2 border px-5 py-3 font-mono text-xs"
          style={{
            borderColor: "var(--color-error)",
            background: "rgba(239, 68, 68, 0.08)",
            color: "var(--color-error)",
          }}
        >
          <strong>Failed:</strong> {progress.error}
          <button onClick={handleCancel} className="ml-4 underline">
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
